import { Navigate, Route, Routes } from "react-router-dom";

import { Account } from "../Account/Account";
import { AdditionalInfo } from "../AdditionalInfo/AdditionalInfo";
import { AnchorScroller } from "UIPalette/ScrollToTop/AnchorScroller";
import { AppLayout } from "UIPalette/AppLayout/AppLayout";
import { Callback } from "../Callback/Callback";
import { Support as SupportView } from "../Support/Support";
import { DatoContentPage } from "UIPalette/Components/DatoContentPage/DatoContentPage";
import { Home } from "../Home/Home";
import { Leaderboards } from "../Leaderboards/Leaderboards";
import { LiveChatProvider } from "../Support/Components/LiveChatProvider";
import { LocaleIdToCodeMap } from "shared/core/locale";
import { LocaleModal } from "UIPalette/LocaleModal/LocaleModal";
import { LoginInfoLayout } from "UIPalette/LoginInfoLayout/LoginInfoLayout";
import { LoginLayout } from "UIPalette/LoginLayout/LoginLayout";
import { ModalProvider } from "shared/core/state/ModalContext/ModalContext";
import { NotFound } from "../NotFound";
import { Preferences } from "../Preferences/Preferences";
import { ProtectedModuleRoute } from "core/auth/accessControl/ProtectedModuleRoute/ProtectedModuleRoute";
import { WisdomModules } from "shared/constants/Modules";
import { changeLanguage } from "i18next";
import { useEffect, useState } from "react";
import useLocale from "core/hooks/useLocale/useLocale";
import { t } from "i18next";
import { useAuthVersioning } from "../../core/hooks/useAuthVersioning";
import { SsoSetupStatus } from "../Account/AccountSecurity/SsoSettings/Components/SsoSetupStatus/SsoSetupStatus";
import { Wellbeing } from "../Wellbeing/Wellbeing";
import { WaterTracker } from "../Wellbeing/WaterTracker/WaterTracker";
import { StepsTracker } from "../Wellbeing/StepsTracker/StepsTracker";
import { SleepTracker } from "../Wellbeing/SleepTracker/SleepTracker";
import { GeneralActivityTracker } from "../Wellbeing/GeneralActivityTracker/GeneralActivityTracker";
import { Resources, ViewResource } from "../Resources/Resources";
import { FourWeekPlans } from "../Resources/FourWeekPlans/FourWeekPlans";
import { ViewFourWeekPlan } from "../Resources/ViewFourWeekPlan/ViewFourWeekPlan";
import { MiniHealthChecks } from "../Resources/MiniHealthChecks/MiniHealthChecks";
import { CompleteMiniHealthCheck } from "../Resources/CompleteMiniHealthCheck/CompleteMiniHealthCheck";
import { BreathingExercises } from "../Resources/BreathingExercises/BreathingExercises";
import { Podcasts, ViewPodcast } from "../Resources/Podcasts/Podcasts";
import { AudioAndVideo, ViewAudioAndVideo } from "../Resources/AudioAndVideo/AudioAndVideo";
import { Article, ViewArticle } from "../Resources/Article/Article";
import { Newsletters, ViewNewsletters } from "../Resources/Newsletters/Newsletters";
import { PersonalDetails } from "../Account/PersonalDetails/PersonalDetails";
import { Preferences as AccountPreferences } from "../Account/Preferences/Preferences";
import { ContactSupport } from "../Account/ContactSupport/ContactSupport";
import { AccountSecurity } from "../Account/AccountSecurity/AccountSecurity";
import { PasswordManagement } from "../Account/AccountSecurity/PasswordManagement/PasswordManagement";
import { SsoSettings } from "../Account/AccountSecurity/SsoSettings/SsoSettings";
import { TwoFactorAuthentication } from "../Account/AccountSecurity/TwoFactorAuthentication/TwoFactorAuthentication";
import { DynamicRedirect } from "../DynamicRedirect/DynamicRedirect";
import { appRedirects } from "../DynamicRedirect/data";
import { Sendgrid } from "../DynamicRedirect/Sendgrid";
import { NavBarContextProvider } from "core/state/NavBarContext/NavBarContext";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { handleSafeAreaChange } from "core/helpers/MobilesafeArea";
import { EqualWebMenuListener } from "../EqualWebMenuListener/EqualWebMenuListener";
import { MoodInsights } from "../MoodInsights/MoodInsights";
import { MoodTrackerContextProvider } from "core/state/Trackers/MoodTrackerContext";
import { DownloadApp } from "../DynamicRedirect/DownloadApp";
import { useAppReadyStatus } from "../../hooks/useAppReadyStatus/useAppReadyStatus";
import Notifications from "../Notifications/Notifications";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { NotificationsContextProvider } from "core/state/NotificationsContext/NotificationsContext";
import { ContentContextProvider } from "core/state/ContentContext/ContentContext";
import { NotifyModalContextProvider } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { AppReviewContextProvider } from "core/state/AppReviewContext/AppReviewContext";
import { SupportMomentsReassuranceContextProvider } from "core/state/SupportMomentsReassuranceContext/SupportMomentsReassuranceContext";
import Unsubscribe from "../Unsubscribe/Unsubscribe";
import { Components } from "../Components/Components";
import { PositiveYou } from "../PositiveYou/PositiveYou";
import { WisdomAcademy } from "../WisdomAcademy/WisdomAcademy";
import { SupportMoments } from "../SupportMoments/SupportMoments";
import { StudentHub } from "../StudentHub/StudentHub";
import { FeatureToggle } from "core/auth/accessControl/FeatureToggle/FeatureToggle";
import { TrackerContextProvider } from "core/state/TrackerContext/TrackerContext";
import { WisdomAI } from "../WisdomAI/WisdomAI";
import { isWisdomAIEnabled } from "../WisdomAI/hooks/useWisdomAIEntry";

export const App = () => {
  const isMobileApp = !!window.flutter_inappwebview;
  useAppReadyStatus();
  const { localeId } = useLocale();
  const { auth, Login, protect } = useAuthVersioning();
  const [safeAreaValue, setSafeAreaValue] = useState(false);
  const equalWebSource = isMobileApp ? "Mobile" : "Web";
  const isLargerDisplay = useMatchMedia(ResponsiveBreakpoints.Desktop);

  const support = (
    <LiveChatProvider>
      <SupportView />
    </LiveChatProvider>
  );

  useEffect(() => {
    document.body.style.backgroundColor = "#fff";

    return () => {
      document.body.style.backgroundColor = "#277E8B";
    };
  }, []);

  useEffect(() => {
    changeLanguage(LocaleIdToCodeMap[localeId]);
  }, [localeId]);

  useEffect(() => {
    handleSafeAreaChange(safeAreaValue);
  }, [safeAreaValue]);

  const handleMenuClose = () => {
    trackEvents(EventNames.EQUALWEB_DRAWER_CLOSE, { equalWebSource });
    setSafeAreaValue(false);
  };

  const handleMenuOpen = () => {
    trackEvents(EventNames.EQUALWEB_DRAWER_OPEN, { equalWebSource });
    setSafeAreaValue(true);
  };

  return (
    <NavBarContextProvider>
      <ModalProvider>
        <EqualWebMenuListener onClose={handleMenuClose} onOpen={handleMenuOpen} />
        <AnchorScroller />
        <LocaleModal />
        <Routes>
          <Route
            element={protect(
              <NotifyModalContextProvider>
                <AppReviewContextProvider>
                  <ContentContextProvider>
                    <NotificationsContextProvider>
                      <TrackerContextProvider>
                        <MoodTrackerContextProvider>
                          <SupportMomentsReassuranceContextProvider>
                            <AppLayout />
                          </SupportMomentsReassuranceContextProvider>
                        </MoodTrackerContextProvider>
                      </TrackerContextProvider>
                    </NotificationsContextProvider>
                  </ContentContextProvider>
                </AppReviewContextProvider>
              </NotifyModalContextProvider>
            )}
          >
            <Route index element={<Navigate replace to="/home" />} />
            <Route path="home" element={<Home />} />
            <Route path="wellbeing" element={<Wellbeing />} />
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.moodTracker} />}>
              <Route path="mood-insights" element={<MoodInsights />} />
            </Route>
            <Route path="notifications" element={!isLargerDisplay ? <Notifications /> : <Navigate replace to="/home" />} />
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.trackers} />}>
              <Route path="water" element={<WaterTracker />} />
              <Route path="steps" element={<StepsTracker />} />
              <Route path="sleep" element={<SleepTracker />} />
              <Route path="general-activity" element={<GeneralActivityTracker />} />
            </Route>
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.leaderBoards} />}>
              <Route path="leaderboards/*" element={<Leaderboards />} />
            </Route>
            <Route path="resources" element={<Resources />} />
            <Route path="resources/:articleId" element={<ViewResource />} />
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.fourWeekPlan} />}>
              <Route path="4-week-plans" element={<FourWeekPlans />} />
              <Route path="4-week-plans/:fourWeekPlanId" element={<ViewFourWeekPlan />} />
            </Route>
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.miniHealthChecks} />}>
              <Route path="mini-health-checks" element={<MiniHealthChecks />} />
              <Route path="mini-health-checks/:miniHealthCheckId" element={<CompleteMiniHealthCheck />} />
            </Route>
            <Route path="breathing-exercises" element={<BreathingExercises />} />
            <Route path="podcasts" element={<Podcasts />} />
            <Route path="podcasts/:articleId" element={<ViewPodcast />} />
            <Route path="audio-and-video" element={<AudioAndVideo />} />
            <Route path="audio-and-video/:articleId" element={<ViewAudioAndVideo />} />
            <Route path="articles" element={<Article />} />
            <Route path="articles/:articleId" element={<ViewArticle />} />
            <Route path="newsletters" element={<Newsletters />} />
            <Route path="newsletters/:articleId" element={<ViewNewsletters />} />
            <Route path="account" element={<Account />} />
            <Route path="preferences" element={<AccountPreferences />} />
            <Route path="personal-details" element={<PersonalDetails />} />
            <Route
              path="terms-and-conditions"
              element={
                <DatoContentPage
                  title={t("pageTitleOverride.terms")}
                  heading={t("account.terms")}
                  slug={"terms"}
                  pageTitle={t("pageTitle.account.terms")}
                />
              }
            />
            <Route
              path="privacy-policy"
              element={<DatoContentPage heading={t("account.privacy")} slug={"privacy"} pageTitle={t("pageTitle.account.privacy")} />}
            />
            <Route path="faqs" element={<DatoContentPage heading={t("account.faqs")} slug={"faqs"} pageTitle={t("pageTitle.account.faqs")} />} />
            <Route
              path="account-cookie-policy"
              element={
                <DatoContentPage
                  title={t("pageTitleOverride.cookiePolicy")}
                  heading={t("account.cookiePolicy")}
                  slug={"cookie-policy"}
                  pageTitle={t("pageTitle.account.cookiePolicy")}
                />
              }
            />
            <Route
              path="about-us"
              element={<DatoContentPage heading={t("account.about")} slug={"about"} pageTitle={t("pageTitle.account.about")} />}
            />
            <Route path="customer-solutions" element={<ContactSupport />} />
            <Route path="account-security" element={<AccountSecurity />} />
            <Route path="password-management" element={<PasswordManagement />} />
            <Route path="single-sign-on" element={<SsoSettings />} />
            <Route path="two-factor-authentication" element={<TwoFactorAuthentication />} />
            <Route path="support" element={isMobileApp ? <DynamicRedirect destination="/mobile-support" /> : support} />
            <Route path="mobile-support" element={isMobileApp ? support : <DynamicRedirect destination="/support" />} />
            <Route path="support/callback" element={<Callback />} />
            <Route path="support-moments" element={<SupportMoments />} />
            <Route path="student-hub" element={<StudentHub />} />
            <Route path="positive-you-journal/*" element={<PositiveYou />} />
            <Route element={<ProtectedModuleRoute wisdomModule={WisdomModules.wisdomAcademy} />}>
              <Route path="wisdom-academy/*" element={<WisdomAcademy />} />
            </Route>
            <Route
              path="wisdom-ai"
              element={
                <FeatureToggle showFeature={isWisdomAIEnabled()}>
                  <WisdomAI />
                </FeatureToggle>
              }
            />
            <Route
              path="components/*"
              element={
                <FeatureToggle showFeature={!process.env.REACT_APP_DISABLE_COMPONENTS_PAGE}>
                  <Components />
                </FeatureToggle>
              }
            />
          </Route>
          <Route element={<LoginLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="unsubscribe" element={<Unsubscribe />} />
          </Route>
          <Route element={<LoginLayout renderWisdomLogo={false} />}>
            <Route path="additional-info" element={protect(<AdditionalInfo />)} />
            <Route path="login/preferences" element={protect(<Preferences />)} />
          </Route>
          <Route path="auth" element={auth()} />
          <Route path="auth/sso" element={auth(true)} />
          <Route path="auth/sso/:provider" element={auth(true)} />
          <Route path="sso/setup" element={<SsoSetupStatus />} />
          <Route path="sendgrid" element={<Sendgrid />} />
          <Route path="download" element={<DownloadApp />} />
          <Route element={<LoginInfoLayout />}>
            <Route path="terms" element={<DatoContentPage title={t("pageTitleOverride.terms")} slug={"terms"} pageTitle={t("pageTitle.terms")} />} />
            <Route path="privacy" element={<DatoContentPage slug={"privacy"} pageTitle={t("pageTitle.privacy")} />} />
            <Route
              path="cookie-policy"
              element={<DatoContentPage title={t("pageTitleOverride.cookiePolicy")} slug={"cookie-policy"} pageTitle={t("pageTitle.cookiePolicy")} />}
            />
            <Route path="login-help" element={<DatoContentPage slug={"login-help"} pageTitle={t("pageTitle.loginHelp")} />} />
          </Route>
          {appRedirects.map((redirect) => (
            <Route
              key={redirect.route}
              path={redirect.route}
              element={<DynamicRedirect paramKey={redirect.paramKey} destination={redirect.destination} />}
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ModalProvider>
    </NavBarContextProvider>
  );
};
